import merge from 'lodash/merge';
import cloneDeep from 'lodash/cloneDeep';
import tinycolor from 'tinycolor2';
import { PROFILE_BG_GRADIENTS, getProfileBoxShadow, getProfileBorderWidth, getProfileBorderRadius } from '../../profile/component-lib.esm.js';
import { getCurrentTheme } from './component-lib.esm.js';

/**
 * Create a new theme
 * @param {Object} theme The new theme settings
 * @returns A valid theme object with your settings merged in
 */
function createTheme(theme) {
  let defaultThemeName = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'campsite';
  const defaultTheme = getCurrentTheme(defaultThemeName);
  return merge({}, cloneDeep(defaultTheme), theme);
}

/**
 * Create a theme from a profile's settings
 * @param {Object} settings The profile settings
 * @returns A valid theme object with your settings merged in
 */
function createThemeFromSettings(settings) {
  let defaultThemeName = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'campsite';
  const settingsObject = settings.theme ? settings.theme : settings;
  const {
    site_bg_color,
    site_bg_gradient_direction,
    site_text_color,
    site_profile_image_border_color,
    site_bg_pattern,
    site_bg_image_filter,
    // fonts
    site_font_family_details = {},
    site_button_font_family_details = {},
    site_title_font_family_details = {},
    // buttons
    site_button_style,
    site_button_font_weight,
    site_button_color,
    site_button_bg_color,
    site_button_border_color,
    site_button_border_width,
    site_button_corner_radius,
    site_button_shadow_style,
    // divider
    site_divider_color,
    // title
    site_title_inherit = true,
    site_title_color,
    site_title_font_weight,
    // social accounts
    site_social_color
  } = settingsObject;

  // Set the fill of the campsite logo
  let logoColorToUse = site_button_bg_color;
  if (site_button_style === 'hollow') {
    logoColorToUse = site_button_color;
  }
  const logoFill = tinycolor(logoColorToUse).setAlpha(1).toRgbString();
  let markFill = tinycolor(logoColorToUse).setAlpha(1);
  markFill = markFill.isDark() ? markFill.lighten(8).toRgbString() : markFill.darken(8).toRgbString();

  // Set the background pattern
  let bgPattern = site_bg_pattern ? `url(${site_bg_pattern})` : 'none';
  if (site_bg_image_filter && site_bg_pattern) {
    const gradient = PROFILE_BG_GRADIENTS[site_bg_image_filter.toUpperCase()];
    bgPattern = `${gradient}, ${bgPattern}`;
  }

  // Calculate btn margin
  let btnMarginBottom = '12px';

  // Get the box shadow settings
  const boxShadowSettings = getProfileBoxShadow(site_button_style, {
    bgColor: site_button_bg_color,
    borderWidth: site_button_border_width,
    borderColor: site_button_border_color,
    shadowStyle: site_button_shadow_style
  });
  return createTheme({
    profile: {
      btnMarginBottom,
      bgColor: site_bg_color,
      bgGradientDirection: site_bg_gradient_direction,
      textColor: site_text_color,
      fontFamily: site_font_family_details.font_family,
      imageBorderColor: site_profile_image_border_color,
      bgPattern,
      // button
      btnBgColor: site_button_bg_color,
      btnColor: site_button_color,
      btnBorderWidth: getProfileBorderWidth(site_button_border_width),
      btnBorderColor: site_button_border_color,
      btnBorderRadius: getProfileBorderRadius(site_button_corner_radius),
      btnFontFamily: site_button_font_family_details.font_family,
      btnFontWeight: site_button_font_weight,
      btnBoxShadow: boxShadowSettings.shadow,
      btnBoxShadowColor: boxShadowSettings.color,
      btnBoxShadowSize: boxShadowSettings.size,
      btnShadowStyle: site_button_shadow_style,
      btnStyle: site_button_style,
      // divider
      dividerColor: site_divider_color,
      // title
      titleColor: site_title_inherit ? site_text_color : site_title_color,
      titleFontFamily: site_title_inherit === false && site_title_font_family_details ? site_title_font_family_details.font_family : site_font_family_details.font_family,
      titleFontWeight: site_title_inherit === false && site_title_font_family_details ? site_title_font_weight : '700',
      // social accounts
      socialColor: site_social_color,
      // Campsite logo
      logoFill,
      markFill
    }
  }, defaultThemeName);
}
export { createTheme, createThemeFromSettings };